/**
 * Client-side API for public film/show data. Use for pages that need to fetch
 * show details by filmId (e.g. /shows/:filmId) in the Vite/React Router app.
 */

export interface PublicFilm {
  id: string
  filmId: string
  title: string
  synopsis: string
  tagline: string
  coverImage: string | null
  thumbnailUrl: string | null
  genre: string | null
  language: string | null
  releaseYear: number | null
  episodeCount: number
  totalViews: number
  rating: number | null
  ratingCount: number | null
  tags: string[]
}

export interface PublicEpisode {
  id: string
  episodeId: string
  filmId: string
  title: string
  description: string
  episodeNumber?: number | null
  seasonNumber?: number | null
  duration: number
  thumbnailUrl: string | null
  premiereAt?: string | null
  status?: string | null
  views: number
  likes: number
}

export interface PublicEpisodesResponse {
  showId: string
  showTitle: string
  episodes: PublicEpisode[]
  nextPremiereAt?: string | null
}

const getBaseUrl = (): string => {
  const url = typeof import.meta !== 'undefined' && (import.meta as any).env?.VITE_API_URL
  return typeof url === 'string' ? url : ''
}

export async function getPublicFilm(filmId: string): Promise<PublicFilm> {
  const base = getBaseUrl()
  if (!base) throw new Error('VITE_API_URL is not set')
  const res = await fetch(`${base}/public/films/${encodeURIComponent(filmId)}`, {
    method: 'GET',
    credentials: 'omit',
    headers: { 'Content-Type': 'application/json' },
  })
  if (!res.ok) {
    if (res.status === 404) throw new Error('Show not found')
    throw new Error('Failed to load show')
  }
  return res.json()
}

export async function getPublicFilmEpisodes(filmId: string): Promise<PublicEpisodesResponse> {
  const base = getBaseUrl()
  if (!base) throw new Error('VITE_API_URL is not set')
  const res = await fetch(`${base}/public/films/${encodeURIComponent(filmId)}/episodes`, {
    method: 'GET',
    credentials: 'omit',
    headers: { 'Content-Type': 'application/json' },
  })
  if (!res.ok) {
    if (res.status === 404) throw new Error('Show not found')
    throw new Error('Failed to load episodes')
  }
  return res.json()
}
